import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";
import { LogIn, UserPlus, Mail, Eye, EyeOff } from "lucide-react";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const activeTab = searchParams.get("tab") === "signup" ? "signup" : "login";
  const returnToParam = searchParams.get("returnTo");
  const returnTo = returnToParam?.startsWith("/") && !returnToParam.startsWith("//") ? returnToParam : "/";
  const hasCreateIntent = returnTo.startsWith("/create");
  const buildAuthUrl = (tab: "login" | "signup") =>
    `/auth?tab=${tab}${returnTo !== "/" ? `&returnTo=${encodeURIComponent(returnTo)}` : ""}`;

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate(returnTo, { replace: true });
      }
    };
    checkUser();
  }, [navigate, returnTo]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Erreur de connexion",
            description: "Email ou mot de passe incorrect",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erreur",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      if (data.user) {
        trackFunnelEvent("auth_login_succeeded", {
          return_to_create: hasCreateIntent,
        });
        toast({
          title: "Connexion réussie",
          description: hasCreateIntent ? "Vous pouvez reprendre votre création." : "Bienvenue !",
        });
        navigate(returnTo, { replace: true });
      }
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la connexion",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const redirectUrl = `${window.location.origin}${returnTo}`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          }
        }
      });

      if (error) {
        if (error.message.includes("already registered")) {
          toast({
            title: "Compte existant",
            description: "Cette adresse email est déjà utilisée. Connectez-vous ou utilisez une autre adresse.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erreur",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      if (data.user && data.user.identities?.length === 0) {
        toast({
          title: "Compte existant",
          description: "Cette adresse email est déjà utilisée. Connectez-vous ou utilisez une autre adresse.",
          variant: "destructive",
        });
        navigate(buildAuthUrl("login"), { replace: true });
        return;
      }

      if (data.user) {
        trackFunnelEvent("auth_signup_submitted", {
          return_to_create: hasCreateIntent,
        });
        setSentEmail(email);
        setEmailSent(true);
      }
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la création du compte",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-12 bg-muted">
      <div className="container mx-auto px-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 text-foreground">
              {hasCreateIntent ? "Dernière étape avant votre création" : "Connexion à "}
              <span className="text-premium">Ma Photo en Tableau</span>
            </h1>
            <p className="text-foreground/70">
              {hasCreateIntent
                ? "Créez un compte gratuit pour sauvegarder vos essais, vos crédits et votre future commande."
                : "Créez votre compte ou connectez-vous pour commander vos œuvres d'art"}
            </p>
          </div>

          {emailSent ? (
            <Card className="bg-white shadow-card border-border">
              <CardContent className="pt-8 pb-8 text-center space-y-4">
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-foreground">Vérifiez votre email</h2>
                <p className="text-foreground/70">
                  Un lien de confirmation a été envoyé à<br />
                  <span className="font-medium text-foreground">{sentEmail}</span>
                </p>
                <p className="text-sm text-foreground/50">
                  Cliquez sur le lien dans l'email pour activer votre compte.
                  {hasCreateIntent ? " Vous serez redirigé vers votre création après confirmation." : ""}
                  {" "}Pensez à vérifier vos spams.
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => { setEmailSent(false); setSentEmail(""); }}
                >
                  Retour à la connexion
                </Button>
              </CardContent>
            </Card>
          ) : (
          <Card className="bg-white shadow-card border-border">
            <CardHeader>
              <CardTitle className="text-center">Accès à votre compte</CardTitle>
            </CardHeader>
            <CardContent>
              {hasCreateIntent ? (
                <div className="mb-5 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm text-foreground/75">
                  <p className="font-medium text-foreground">Pourquoi créer un compte ?</p>
                  <ul className="mt-2 space-y-1">
                    <li>✓ Retrouver vos générations et vos crédits</li>
                    <li>✓ Passer commande plus vite après avoir choisi le rendu</li>
                    <li>✓ Suivre votre tableau personnalisé jusqu'à la livraison</li>
                  </ul>
                </div>
              ) : null}
              <Tabs value={activeTab} onValueChange={(v) => navigate(buildAuthUrl(v as "login" | "signup"), { replace: true })} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" data-value="login">
                    <LogIn className="w-4 h-4 mr-2" />
                    Connexion
                  </TabsTrigger>
                  <TabsTrigger value="signup">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Inscription
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        autoComplete="email"
                        placeholder="votre@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full" 
                      variant="premium"
                      disabled={isLoading}
                    >
                      {isLoading ? "Connexion..." : "Se connecter"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Nom complet</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        autoComplete="name"
                        placeholder="Votre nom complet"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        autoComplete="email"
                        placeholder="votre@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={6}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full" 
                      variant="premium"
                      disabled={isLoading}
                    >
                      {isLoading ? "Création..." : "Créer mon compte"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;